// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { UseQueryResult } from '@tanstack/react-query';
import { CredentialOffer } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Offer';
import { useLedgerApiClient } from '../../../../contexts/LedgerApiContext';
import { useParties } from '../../../../contexts/PartiesContext';
import TemplateContract from '../../../../utils/TemplateContract';
import { useQueryWithOptions } from '../../../other/useQueryWithOptions';

const useCredentialOffers = <T = TemplateContract<CredentialOffer>[]>(
  // Accept the user's select function as an argument
  select?: (data: TemplateContract<CredentialOffer>[]) => T,
): UseQueryResult<T, string> => {
  const ledgerApi = useLedgerApiClient();
  const { operator } = useParties();

  return useQueryWithOptions({
    queryKey: ['queryCredentialOffers', operator],
    enabled: !!operator,

    // Here is where the layering happens
    select: (data: TemplateContract<CredentialOffer>[]) => {
      // 1. Always apply the base filter first
      const baseFiltered = data.filter((d) => d.payload.operator === operator);

      // 2. If a custom select is provided, run it on the already filtered data
      if (select) {
        return select(baseFiltered);
      }

      // 3. Otherwise, return the base filtered data
      // We cast 'as unknown as T' because TS doesn't know T is the array type
      // in the case where 'select' is undefined.
      return baseFiltered as unknown as T;
    },

    queryFn: async (_, options) => {
      return ledgerApi.query(options, CredentialOffer);
    },
  });
};

export default useCredentialOffers;
